import React, { useContext, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import DropdownMenu from 'core/modules/Partials/DropdownMenu/DropdownMenu';
import { useGetCategories } from 'core/modules/Navigation/NavCategories/hooks/useGetCategories';
import { RECOMMENDED, ALPHABETICAL, EARN_RATE } from 'core/modules/DynamicMerchants/utils/sortTypes';
import { StickyContext } from 'core/modules/Partials/StickyMenu/context/StickyContext';
import { useExpandButtonOnStickyMenu } from 'core/modules/Partials/StickyMenu/hooks/useExpandButtonOnStickyMenu';
import { scrollToElement } from 'core/utils/helpers/scrollToElement';
import { triggerFilterIsChangedGAEvent } from 'core/modules/DynamicMerchants/actions';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';

import './Navigation.scss';

function Navigation({
  merchants, setActiveSort, activeSort, activeFilter, setActiveFilter, merchantsToShow, title,
}) {
  const dispatch = useDispatch();
  const navRef = useRef();
  const { categories } = useGetCategories();
  const isMobileMQ = useSelector(selectIsMobileMQ);

  const recommendedButtonText = 'Recommended';
  const alphabeticalButtonText = isMobileMQ ? 'A-Z' : 'Alphabetical';
  const rateButtonText = isMobileMQ ? 'Rate' : 'Earn rate';

  const getCategoriesDropDown = (categories, merchants) => {
    const collectUniqueMerchantIds = () => {
      const ids = merchants.map(({ merchant }) => merchant.categories.map(({ id }) => (id)));
      return [...new Set(ids.flat())];
    };

    const uniqueMerchantIds = collectUniqueMerchantIds();

    const dropdownCategories = categories
      .filter((category) => (uniqueMerchantIds.includes(category.id))) // filter out categories without merchants
      .map((category) => ({ value: category.id, label: category.name }));

    return [
      { value: 'default', label: 'Select a category', className: 'mn_hidden' },
      { value: 'all', label: 'All categories' },
      ...dropdownCategories,
    ];
  };

  const categoriesDropDown = getCategoriesDropDown(categories, merchants);
  const handleActiveClassName = (sortBy) => (activeSort === sortBy ? 'mn_active' : '');

  const {
    isSticky, stickyClassName, stickyHeaderHeight, stickyMenuRef,
  } = useContext(StickyContext);
  const { shouldExpandStickyMenu, expandStickyMenu } = useExpandButtonOnStickyMenu();

  const scrollToNavigation = () => setTimeout(() => { scrollToElement(stickyMenuRef.current, 300); }, 0);

  const handleFilter = (target) => {
    setActiveFilter(target.value);
    isSticky && scrollToNavigation();
  };

  const handleSort = (value) => {
    setActiveSort(value);
    isSticky && scrollToNavigation();
  };

  useEffect(() => {
    if (!activeFilter) {
      return;
    }

    // eslint-disable-next-line eqeqeq
    const selectedFilterName = categoriesDropDown.find(({ value }) => value == activeFilter)?.label;
    const filteredMerchantsNumber = merchantsToShow.length;

    dispatch(triggerFilterIsChangedGAEvent(selectedFilterName, filteredMerchantsNumber, title));
  }, [activeFilter]);

  if (isSticky && !shouldExpandStickyMenu) {
    return (
      <button className="mn_dynamicMerchantExpandButton" onClick={expandStickyMenu}>
        Sort & filter <span className="mn_icon">+</span>
      </button>
    );
  }

  return (
    <div
      className={`mn_dynamicMerchantNavigation ${stickyClassName}`}
      style={{ top: `${stickyHeaderHeight}px` }}
      ref={navRef}
    >
      <div className="mn_navSection mn_sortSection">
        <div className="mn_label">Sort by<span className="mn_colon">:</span></div>
        <div className="mn_sortButtons">
          <button
            className={handleActiveClassName(RECOMMENDED)}
            onClick={() => handleSort(RECOMMENDED)}
            data-sort-type={RECOMMENDED}
          >
            {recommendedButtonText}
          </button>
          <button
            className={handleActiveClassName(ALPHABETICAL)}
            onClick={() => handleSort(ALPHABETICAL)}
            data-sort-type={ALPHABETICAL}
          >
            {alphabeticalButtonText}
          </button>
          <button
            className={handleActiveClassName(EARN_RATE)}
            onClick={() => handleSort(EARN_RATE)}
            data-sort-type={EARN_RATE}
          >
            {rateButtonText}
          </button>
        </div>
      </div>
      <div className="mn_navSection mn_filterSection">
        <div className="mn_label">Filter by<span className="mn_colon">:</span></div>
        <DropdownMenu
          dynamicDefaultOption={activeFilter}
          customIdName="mn_selectCategory"
          customClassName="mn_selectCategory"
          options={categoriesDropDown}
          onChangeCallback={(e) => handleFilter(e.target)}
        />
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><Navigation {...props} /></ComponentErrorBoundary>;
